"use client";

import { useEffect, useState, useCallback } from "react";
import { getUserGuideStep, updateUserGuideStep } from "@/lib/userGuide";

const COMPLETED_KEY = "UserGuideCompleted";

export function useUserGuideStep() {
  const [completed, setCompleted] = useState<number>(4); // Default to completed to avoid showing guide by default
  const [isLoading, setIsLoading] = useState(true);

  // 先从 localStorage 读取，其次再从数据库读取
  const readCompleted = useCallback(async (): Promise<number> => {
    // 首先尝试从 localStorage 获取以获得即时响应
    try {
      const localValue = localStorage.getItem(COMPLETED_KEY);
      if (localValue !== null) {
        const n = parseInt(localValue, 10);
        if (!isNaN(n)) {
          return n;
        }
      }
    } catch {
      // 忽略 localStorage 错误
    }

    // 然后尝试从数据库获取
    try {
      const dbValue = await getUserGuideStep();
      localStorage.setItem(COMPLETED_KEY, String(dbValue)); // Cache in localStorage
      return dbValue;
    } catch {
      return 0;
    }
  }, []);

  // 同时写入 localStorage 和数据库
  const writeCompleted = useCallback(async (step: number): Promise<void> => {
    // 立即写入 localStorage 以提供响应式 UI
    try {
      localStorage.setItem(COMPLETED_KEY, String(step));
    } catch {
      // Ignore localStorage errors
    }

    // 在后台更新数据库
    try {
      await updateUserGuideStep(step);
    } catch (error) {
      console.error("Failed to update user guide step in database:", error);
    }
  }, []);

  // 从存储初始化引导步骤
  useEffect(() => {
    readCompleted().then((step) => {
      setCompleted(step);
      setIsLoading(false);
    });
  }, [readCompleted]);

  return {
    completed,
    setCompleted,
    writeCompleted,
    isLoading,
  };
}
